import React, { useRef, useEffect } from 'react';

import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { useIntl } from 'react-intl';

// oxlint-disable-next-line import/no-cycle
import { ActiveImports, IMPORT_FOLDER_NAMES } from '../importStrategyDefinitions.ts';
import type { ImportStrategyComponentType } from '../../types.ts';

const wrapperStyles = cssMap({
	root: {
		width: '100%',
		position: 'relative',
	},
});

//TODO: add BasicStrategy component
export const ImportFromLocal: ImportStrategyComponentType = ({
	onStartStrategy,
	onDocumentSelect,
	registerTrigger,
	children,
}) => {
	const { formatMessage } = useIntl();
	const localInputRef = useRef<HTMLInputElement>(null);

	const pickDocument = () => {
		localInputRef.current!.dispatchEvent(
			new MouseEvent(`click`, {
				//event must not be bubbled to prevent a callback loop error
				bubbles: false,
				cancelable: true,
				view: window,
			}),
		);
	};

	//TODO: create base strategy component and remove the duplication
	//due to stale closure we have to keep the props in a ref, so the methods are always up to date after rerender
	// another approach is to move ImportContextStrategy into every strategy and extend it with the props.
	const strategyProps = {
		key: ActiveImports.IMPORT_FROM_LOCAL,
		folderName: formatMessage(IMPORT_FOLDER_NAMES[ActiveImports.IMPORT_FROM_LOCAL]),
		pickDocument,
	};
	const strategyPropsRef = useRef(strategyProps);
	strategyPropsRef.current = strategyProps;

	const handleClick = () => {
		onStartStrategy(strategyPropsRef);
	};

	// Expose the same entry point the card's onItemClick uses, so consumers that render
	// their own trigger (e.g. the onboarding CTA dropdown) can start the import without
	// this component owning the clickable element. This must stay synchronous so the
	// hidden file input below is still clicked inside the trusted user gesture.
	useEffect(() => {
		registerTrigger?.({ start: () => onStartStrategy(strategyPropsRef) });
	}, [registerTrigger, onStartStrategy]);

	const handleSelectedDocuments = (documents: File[]) => {
		onDocumentSelect(documents);
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		event.stopPropagation();
		event.preventDefault();

		const { files } = event.target;
		const fileList: File[] = Array.from(files!);
		localInputRef.current!.value = '';
		handleSelectedDocuments(fileList);
	};

	return (
		/**
		 * Non-interactive wrapper that contains the hidden file input to avoid nested interactive elements accessibility violation.
		 * https://dequeuniversity.com/rules/axe/4.10/nested-interactive?application=axeAPI%E2%80%8B
		 */
		<div css={wrapperStyles.root}>
			<input
				type="file"
				ref={localInputRef}
				style={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					visibility: 'hidden',
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					position: 'absolute',
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					top: token('space.negative.400'),
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					width: token('space.100'),
				}}
				accept=".docx"
				onChange={handleFileChange}
				multiple
				data-testid="import-from-local-file-input"
			/>
			{/* delegate click handling to child components */}
			{React.cloneElement(children as React.ReactElement, {
				...(children as React.ReactElement).props,
				// SourcesListCard triggers analytics with onItemClick prop
				onItemClick: (data: any) => {
					handleClick();
					const originalOnItemClick = (children as React.ReactElement).props.onItemClick;
					if (originalOnItemClick) {
						originalOnItemClick(data);
					}
				},
				// Remaining callbacks (notably AvatarItem in SourceListCard)
				onClick: (event: React.MouseEvent) => {
					handleClick();
					const originalOnClick = (children as React.ReactElement).props.onClick;
					if (originalOnClick) {
						originalOnClick(event);
					}
				},
			})}
		</div>
	);
};
